import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { League, Player, Team, Contract, ContractStatus, GlobalConfig, LeaguePhase } from '../../../sdk/model';
import { useAppSelector } from '../../../app/hooks';
import { useLoadLeagueData } from '../../../app/dataLoaderHooks';
import { findDefaultTeam } from '../draft/service';
import { 
  Typography, 
  Button, 
  Paper, 
  TableContainer, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Divider,
  Stack,
  Card,
  CardContent
} from '@mui/material';
import { toSalary } from '../../util/SalaryDisplay';
import { contractApi, playerClaimApi } from '../../../adapters/APIExporter';
import EventActivityDisplay from '../leagueEvent/EventActivityDisplay';
import ContractSeasonDisplay from './ContractSeasonDisplay';
import DropPlayerButton from '../team/DropPlayerButton';
import ActivatePlayerButton from '../team/ActivatePlayerButton';
import TeamDisplay from '../team/TeamDisplay';
import { isPlayerLocked } from '../../util/PlayerDataUtil';

const ACTIVE_LEAGUE_PHASES = new Set([LeaguePhase.RegularSeason, LeaguePhase.Playoffs]);

interface ContractDetailsProps {
  leagueId: string;
  player: Player | undefined;
  contract: Contract;
}

const ContractDetails: React.FC<ContractDetailsProps> = ({ leagueId, player, contract }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isException, setIsException] = React.useState(false);
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [isPoachingAllowed, setIsPoachingAllowed] = React.useState(false);
  const [history, setHistory] = React.useState(null);
  
  const globalConfig: GlobalConfig = useAppSelector((state) => state.globalConfigDataLoad);
  const user = useAppSelector((state) => state.user);
  
  const league: League | undefined = useLoadLeagueData({
    leagueId,
    userId: user.id,
    loadBasicDraftData: true,
    loadFullDraftData: false,
    loadContractData: false,
    loadBasicAuctionData: true,
    loadFullAuctionData: false,
    loadRosterData: false,
    isException,
    setIsException,
    isLoadComplete,
    setLoadComplete
  });
  
  React.useEffect(() =>{
      playerClaimApi.isPoachingAllowed().then(response => {
        setIsPoachingAllowed(response.data)
      })
    }, [])
  
  const teamMap: Map<string | undefined, Team> = new Map(league?.teams?.map((team) => [team.id, team]));
  const myDefaultTeam = league?.teams ? teamMap.get(findDefaultTeam(league.teams, user?.id)) : null;
  
  const loadContractHistory = React.useCallback(() => {
    contractApi.getContractHistory(contract.id).then(response => setHistory(response.data));
  }, [contract.id]);
  
  const getWaiverClearDate = React.useCallback(() => {
    if (contract.waiverExpirationDate) {
      return new Date(contract.waiverExpirationDate);
    }
    const waiverClearDate = new Date(globalConfig.nextWeekBeginDate!);
    waiverClearDate.setDate(waiverClearDate.getDate() + 1);
    return waiverClearDate;
  }, [contract.waiverExpirationDate, globalConfig.nextWeekBeginDate]);
  
  const team = teamMap.get(contract.teamId)!;
  const myTeam = team && team.owners!.some(owner => owner.id === user.id);
  const waiverClearDate = getWaiverClearDate();
  const waiverWeekday = waiverClearDate.toLocaleDateString(undefined, { weekday: 'long' });
  const waiverMmDd = waiverClearDate.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit' 
    });

  const renderContractActions = () => {
    if (myTeam) {
      if (contract.status === ContractStatus.Active) {
        return (
          <Stack direction={isMobile ? 'column' : 'row'} spacing={1} mt={isMobile ? 1 : 0}>
            <DropPlayerButton 
              leagueId={leagueId} 
              player={player!} 
              contract={contract} 
              currentSeasonYear={league?.currentSeasonYear!} 
              team={team} 
              callback={() => true}
              sx={{ width: isMobile ? '100%' : 'auto' }}
            />
            {contract.practiceSquad && (
              <ActivatePlayerButton 
                leagueId={leagueId} 
                player={player!} 
                contract={contract} 
                currentSeasonYear={league?.currentSeasonYear!} 
                team={team} 
              />
            )}
          </Stack>
        );
      }
    } else if (contract.status === ContractStatus.Active) {
      const locked = ACTIVE_LEAGUE_PHASES.has(league?.phase!) && (!isPoachingAllowed || isPlayerLocked(player));
      return contract.practiceSquad ? (
        <div title={locked ? "Players are locked from poaching from their kickoff time until 24 hours after the start of the following week" : ""}><Button 
          fullWidth={isMobile}
          color="primary" 
          variant="contained" 
          disabled={locked}
          onClick={() => navigate(`/league/${league?.id}/team/${myDefaultTeam?.id}/poach/${contract.playerId}`)}
        >
          Poach
        </Button></div>
      ) : (
        <Button 
          fullWidth={isMobile}
          color="primary" 
          variant="contained" 
          onClick={() => navigate(`/league/${league?.id}/trade?teamId=${team.id}&playerId=${contract.playerId}`)}
        >
          Propose Trade
        </Button>
      );
    } else if (contract.status === ContractStatus.Waived && !myTeam) {
      return (
        <Button 
          fullWidth={isMobile}
          color="primary" 
          variant="contained" 
          onClick={() => navigate(`/league/${leagueId}/team/${myDefaultTeam?.id}/waivers/${contract.playerId}`)}
          sx={{ mt: isMobile ? 1 : 0 }}
        >
          Claim Waived Contract
        </Button>
      );
    }
    return null;
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" component="div">
              <Box component="span" fontWeight="bold">Status: </Box>
              {contract.status}
              <Box component="span" sx={{ mx: 1 }}>•</Box>
              <Box component="span" fontWeight="bold">Team: </Box>
              <TeamDisplay leagueId={leagueId} team={team} emphasized={myTeam} teamId={contract.teamId} />
              <Box component="span" sx={{ mx: 1 }}>•</Box>
              <Box component="span" fontWeight="bold">Value: </Box>
              {toSalary(contract.totalSalary)}
              {contract.practiceSquad && " (PS)"}
              <Box component="span" sx={{ mx: 1 }}>•</Box>
              <Box component="span" fontWeight="bold">Remaining {league?.currentSeasonYear} Cap Hit: </Box>
              {toSalary(contract.seasons?.filter(season => season.season_year === league?.currentSeasonYear).reduce((sum, season) => sum + season.weeks!.reduce((weeklySum, week) => weeklySum + week.payments!.filter(payment => !payment.completed && payment.teamId).reduce((paymentSum, payment) => paymentSum + payment.price!, 0), 0), 0))}
            </Typography>
            
            {contract.status === ContractStatus.Waived && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                <Box component="span" fontWeight="bold">Waiver clears on: </Box>
                {waiverWeekday + " (" + waiverMmDd + ")"}
              </Typography>
            )}
          </Grid>
          
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
            {renderContractActions()}
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, overflowX: 'auto' }}>
          <TableContainer component={Paper}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Season</TableCell>
                  <TableCell>Total Value</TableCell>
                  <TableCell>Guaranteed</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Remaining</TableCell>
                  <TableCell>Rem. Guaranteed</TableCell>
                  <TableCell>Rem. Non-Guaranteed</TableCell>
                  <TableCell>Team</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contract.seasons?.map(contractSeason => (
                  <ContractSeasonDisplay 
                    key={contractSeason.season_year} 
                    contractSeason={contractSeason} 
                    teamMap={teamMap} 
                    isMobile={isMobile} 
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {history ? (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>Transaction History</Typography>
            <TableContainer component={Paper}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Transaction</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((eventActivity, eventIndex) => (
                    <EventActivityDisplay key={eventIndex} activity={eventActivity} teamMap={teamMap} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : contract.playerId ? (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={loadContractHistory}
              size={isMobile ? 'small' : 'medium'}
            >
              Show Transaction History
            </Button>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default ContractDetails;
